import { useQuery } from 'convex/react';
import { api } from '../../convex/_generated/api';
import type { Id } from '../../convex/_generated/dataModel';
import { useConvexMutation } from './use-convex-mutation';

// ════════════════════════════════════════════════════════════
// TYPES
// ════════════════════════════════════════════════════════════

export interface Goal {
  id: string;
  type: 'short_term' | 'long_term';
  category: 'technical' | 'tactical' | 'physical' | 'mental';
  title: string;
  description: string | null;
  targetDate: string | null;
  status: 'active' | 'completed' | 'cancelled';
  completedAt: number | null;
  completionNotes: string | null;
  createdAt: number;
  updatedAt?: number;
  createdByUserId?: string | null;
}

export interface GoalsResponse {
  goals: Goal[];
}

export interface GoalsWithStatsResponse {
  goals: Goal[];
  stats: {
    total: number;
    active: number;
    completed: number;
    cancelled: number;
  };
}

export interface CreateGoalData {
  type: 'short_term' | 'long_term';
  category: 'technical' | 'tactical' | 'physical' | 'mental';
  title: string;
  description?: string;
  targetDate?: string;
}

export interface UpdateGoalData {
  type?: 'short_term' | 'long_term';
  category?: 'technical' | 'tactical' | 'physical' | 'mental';
  title?: string;
  description?: string | null;
  targetDate?: string | null;
  status?: 'active' | 'completed' | 'cancelled';
  completionNotes?: string | null;
}

// ════════════════════════════════════════════════════════════
// COACH HOOKS
// ════════════════════════════════════════════════════════════

export function usePlayerGoals(playerId: string) {
  const data = useQuery(
    api.coach.goals.listForPlayer,
    playerId ? { playerId: playerId as Id<"players"> } : 'skip'
  );
  return { data, isLoading: data === undefined, error: null };
}

export function useCreateGoal(playerId: string) {
  const { mutateAsync, isPending } = useConvexMutation(api.coach.goals.create);
  return {
    isPending,
    mutateAsync: (data: CreateGoalData) => mutateAsync({ playerId: playerId as Id<"players">, ...data }),
  };
}

export function useUpdateGoal() {
  const { mutateAsync, isPending } = useConvexMutation(api.coach.goals.update);
  return {
    isPending,
    mutateAsync: (args: { goalId: string; data: UpdateGoalData }) =>
      mutateAsync({
        goalId: args.goalId as Id<"playerGoals">,
        type: args.data.type,
        category: args.data.category,
        title: args.data.title,
        description: args.data.description ?? undefined,
        targetDate: args.data.targetDate ?? undefined,
        status: args.data.status,
        completionNotes: args.data.completionNotes ?? undefined,
      }),
  };
}

export function useDeleteGoal() {
  const { mutateAsync, isPending } = useConvexMutation(api.coach.goals.remove);
  return {
    isPending,
    mutateAsync: (goalId: string) => mutateAsync({ goalId: goalId as Id<"playerGoals"> }),
  };
}

// ════════════════════════════════════════════════════════════
// PARENT HOOKS
// ════════════════════════════════════════════════════════════

export function useChildGoals(childId: string) {
  const data = useQuery(
    api.parent.goals.listForChild,
    childId ? { childId: childId as Id<"players"> } : 'skip'
  );
  return { data, isLoading: data === undefined, error: null };
}
